/**
 * Hook d'installation PWA — capture `beforeinstallprompt` pour proposer
 * l'installation via un vrai bouton dans l'UI (l'icône native de Chrome dans
 * la barre d'adresse est invisible pour notre cible terrain).
 *
 * Trois situations :
 *  - Chrome/Edge (Android + desktop) : événement capturé → installation 1 tap.
 *  - iOS Safari : pas d'API d'installation — on détecte la plateforme pour
 *    afficher un guide manuel (Partager → Sur l'écran d'accueil).
 *  - Déjà installée (mode standalone) : on ne propose plus rien.
 */

import { useEffect, useState } from "react";

/** Événement Chrome non typé par TS (spec non finalisée). */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

// Capturé au chargement du bundle : l'événement peut être émis par le
// navigateur AVANT le montage des composants React (il ne sera pas ré-émis).
let evenementInstallation: BeforeInstallPromptEvent | null = null;
const abonnes = new Set<() => void>();

function notifierAbonnes() {
  abonnes.forEach((notifier) => notifier());
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    // Bloque la mini-infobar automatique de Chrome Android : c'est notre
    // bouton qui déclenchera l'installation, au moment choisi par l'utilisateur.
    event.preventDefault();
    evenementInstallation = event as BeforeInstallPromptEvent;
    notifierAbonnes();
  });
  window.addEventListener("appinstalled", () => {
    evenementInstallation = null;
    notifierAbonnes();
  });
}

/** L'app tourne déjà en mode installé (icône écran d'accueil). */
function estModeStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // iOS Safari expose `navigator.standalone` (hors spec).
    (navigator as { standalone?: boolean }).standalone === true
  );
}

/** iPhone/iPad — y compris iPadOS 13+ qui se déclare "MacIntel" mais tactile. */
function estAppareilIos(): boolean {
  return (
    /iPhone|iPad|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

export function useInstallationPwa() {
  // Re-render quand l'événement d'installation arrive/disparaît.
  const [, forcerRendu] = useState(0);
  useEffect(() => {
    const notifier = () => forcerRendu((n) => n + 1);
    abonnes.add(notifier);
    return () => {
      abonnes.delete(notifier);
    };
  }, []);

  const estInstallee = estModeStandalone();
  const peutInstallerNativement = evenementInstallation !== null && !estInstallee;
  const estIos = estAppareilIos() && !estInstallee;

  return {
    estInstallee,
    /** Installation native 1 tap disponible (Chrome/Edge). */
    peutInstallerNativement,
    /** iOS : proposer le guide manuel à la place. */
    estIos,
    /** Faut-il afficher une proposition d'installation quelque part ? */
    proposerInstallation: peutInstallerNativement || estIos,
    /** Ouvre la boîte de dialogue d'installation du navigateur. */
    lancerInstallation: async () => {
      if (!evenementInstallation) return;
      await evenementInstallation.prompt();
      const choix = await evenementInstallation.userChoice;
      if (choix.outcome === "accepted") {
        evenementInstallation = null;
        notifierAbonnes();
      }
    },
  };
}
